import { AthosCollectionProvider } from '../fetchers/collection'
import { BeaconAnalyticsProvider, useBeaconLogImpressions, useBeaconLogRender } from '../hooks/useBeaconAnalytics'
import { useScrollCache } from '../hooks/useScrollCache'
import { scrollHistoryManager } from '@/components/helpers/scroll-history'
import { useFilterScroll } from '../hooks/useFilterScroll'
import { useAthosContext } from '../fetchers/provider'
import { useMerchedResults } from '../hooks/useMerchedResults'
import { ResultsCard } from './ResultCard'
import { CollectionToolbar } from './CollectionToolbar'
import { FilterDrawer } from './FilterDrawer'
import { Pagination } from './Pagination'
import { useEffect, useState, useMemo, useRef } from 'preact/hooks'
import useOfflineWishlist from '@/components/hooks/useOfflineWishlist'
import { SkeletonCard } from './SkeletonCard'
import { useWishlist } from '@/components/hooks/useWishlist'

const DEFAULT_PER_PAGE = 24

function CollectionGrid() {
  history.scrollRestoration = 'manual'
  const { isLoading } = useAthosContext()
  const currentResults = useMerchedResults()
  const [firstLoad, setFirstLoad] = useState(true)
  const hasStartedLoading = useRef(false)
  const { setScrollElement } = useFilterScroll()
  const [beaconNode, setBeaconNode] = useState()

  useBeaconLogRender()
  useBeaconLogImpressions(beaconNode)
  const { wishlist: offlineWishlist, getKeys, ...callbacks } = useOfflineWishlist()
  const { wishlists: omneoWishlists } = useWishlist()

  const wishlistItems = useMemo(() => {
    return getKeys()
  }, [offlineWishlist])

  useEffect(() => {
    if (isLoading) hasStartedLoading.current = true
    if (!isLoading && hasStartedLoading.current) setFirstLoad(false)
  }, [isLoading])

  useScrollCache([currentResults, firstLoad])

  const filtersOpenByDefault = useRef(typeof window !== 'undefined' && window.innerWidth >= 1024)
  const stickyFilters = GM_STATE?.plp?.collectionToolbar !== 'non-sticky'
  const perPage = settings?.per_page || DEFAULT_PER_PAGE
  const showSkeleton = firstLoad && (isLoading || currentResults.length === 0)

  return (
    <x-modal
      className="group/filters flex w-full flex-col"
      data-id="filters"
      data-ui={filtersOpenByDefault.current ? 'visible' : undefined}
    >
      <CollectionToolbar sticky={stickyFilters} />
      <div
        data-plp-scroll-target
        ref={(el) => {
          setScrollElement(el)
          setBeaconNode(el)
        }}
        className="flex w-full md:py-10"
      >
        <FilterDrawer nested showToggle={false} stickyFilters={stickyFilters} />
        <div className="flex min-w-0 flex-1 flex-col md:px-10">
          <div className="grid h-fit grid-cols-2 gap-x-2 gap-y-6 md:grid-cols-4 md:gap-x-4 md:group-data-visible/filters:grid-cols-3">
            {showSkeleton ? (
              Array.from({ length: perPage }).map((_, i) => <SkeletonCard className={'transition-all duration-300'} key={i} />)
            ) : (
              currentResults?.map((item, index) => {
                return 'value' in item ? (
                  <div
                    className="relative col-span-full aspect-square md:col-span-1"
                    key={item.value}
                  >
                    <div
                      dangerouslySetInnerHTML={{ __html: item.value }}
                      className="absolute inset-0"
                    />
                  </div>
                ) : (
                  <ResultsCard
                    wishlist={{
                      omneoWishlists,
                      offlineWishlistItems: wishlistItems,
                      wsCallbacks: callbacks,
                      ...(item.handle.includes('gift-card') && { type: 'none' })
                    }}
                    pageType="collection"
                    result={item}
                    index={index}
                    key={item.handle}
                    className="wk-wishlist-button-target"
                  />
                )
              })
            )}
          </div>
          <Pagination />
        </div>
      </div>
    </x-modal>
  )
}

export function WrappedCollectionGrid() {
  scrollHistoryManager.updateHistoryState()

  return (
    <AthosCollectionProvider handle={collection?.handle} resultsPerPage={settings?.per_page || DEFAULT_PER_PAGE}>
      <BeaconAnalyticsProvider type="category">
        <CollectionGrid />
      </BeaconAnalyticsProvider>
    </AthosCollectionProvider>
  )
}
